import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import RsvpEventHeader from '../components/rsvp/RsvpEventHeader';
import RsvpSuccessCard from '../components/rsvp/RsvpSuccessCard';
import RsvpTermsModal from '../components/rsvp/RsvpTermsModal';
import { trackLead } from '../lib/metaPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TERMS = `By submitting, you confirm:
1. You meet the event's minimum age requirement.
2. The details you provide are accurate.
3. You agree to abide by venue rules and the door team's decisions.
4. You consent to Unight & the promoter contacting you about this event.
5. This RSVP is non-transferable and may be revoked at the door for safety reasons.`;

/**
 * Public RSVP page reached by a guest list's shareable code (e.g. /rsvp/UN-7K2X).
 * Phase 1 PRD: birth-year age gate, mandatory email + phone, T&C consent.
 */
const RsvpPage = () => {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [birthDate, setBirthDate] = useState('');  // YYYY-MM-DD
  const [partyType, setPartyType] = useState('single');  // 'single' | 'couple'
  const [gender, setGender] = useState('');  // single: male|female ; couple: male_female|female_female
  const [partnerName, setPartnerName] = useState('');
  const [partnerPhone, setPartnerPhone] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerBirthDate, setPartnerBirthDate] = useState('');
  const [agreedEligibility, setAgreedEligibility] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [qrDataUri, setQrDataUri] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/rsvp/${code}`);
        if (active) setInfo(res.data);
      } catch (e) {
        if (active) setError(e.response?.data?.detail || 'Invalid code');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [code]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const minAge = info?.event_min_age ?? 21;
  const termsText = info?.event_terms_conditions || DEFAULT_TERMS;
  const isCustomTerms = Boolean(info?.event_terms_conditions);

  // Compute calculated age from full DOB — precise, not just year-based
  const calculatedAge = useMemo(() => {
    if (!birthDate) return null;
    const bd = new Date(birthDate);
    if (isNaN(bd.getTime())) return null;
    const today = new Date();
    let years = today.getFullYear() - bd.getFullYear();
    const mDelta = today.getMonth() - bd.getMonth();
    if (mDelta < 0 || (mDelta === 0 && today.getDate() < bd.getDate())) years -= 1;
    return years;
  }, [birthDate]);

  const ageBlocked = calculatedAge !== null && calculatedAge < minAge;

  // Build a display string from event_date (YYYY-MM-DD) + event_time (HH:MM) without
  // triggering timezone conversion. `new Date('2026-07-04')` is parsed as UTC midnight
  // by JS and then rendered in the local zone — which is why IST users were seeing
  // 05:30 AM for a 10 PM event. Splitting the parts and using the local-time Date
  // constructor keeps the wall-clock intact.
  const eventWhen = useMemo(() => {
    const dateStr = info?.event_date;
    const timeStr = info?.event_time;
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
    if (!y || !m || !d) return dateStr;
    const dt = new Date(y, m - 1, d, hh || 0, mm || 0);
    const datePart = dt.toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });
    if (!timeStr) return datePart;
    const timePart = dt.toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    return `${datePart} · ${timePart}`;
  }, [info?.event_date, info?.event_time]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !email.trim()) {
      toast.error('Name, phone and email are required');
      return;
    }
    if (!birthDate) {
      toast.error('Enter your date of birth');
      return;
    }
    if (calculatedAge === null || calculatedAge < 0 || calculatedAge > 150) {
      toast.error('Enter a valid date of birth');
      return;
    }
    if (calculatedAge < minAge) {
      toast.error(`This event is ${minAge}+. Sorry, you don't meet the age requirement.`);
      return;
    }
    if (partyType === 'single' && !['male', 'female'].includes(gender)) {
      toast.error('Please select your gender');
      return;
    }
    if (partyType === 'couple') {
      if (!['male_female', 'female_female'].includes(gender)) {
        toast.error('Choose the couple type (male+female or female+female)');
        return;
      }
      if (!partnerName.trim()) {
        toast.error('Add the second person\u2019s name');
        return;
      }
      if (!partnerPhone.trim()) {
        toast.error('Add the second person\u2019s mobile (WhatsApp)');
        return;
      }
      if (!partnerBirthDate) {
        toast.error('Add the second person\u2019s date of birth');
        return;
      }
      // Age gate for partner
      const pbd = new Date(partnerBirthDate);
      if (!isNaN(pbd.getTime())) {
        const today = new Date();
        let pAge = today.getFullYear() - pbd.getFullYear();
        const md = today.getMonth() - pbd.getMonth();
        if (md < 0 || (md === 0 && today.getDate() < pbd.getDate())) pAge -= 1;
        if (pAge < minAge) {
          toast.error(`Partner is under ${minAge} — both people must meet the age requirement.`);
          return;
        }
      }
    }
    if (!agreedEligibility || !agreedTerms) {
      toast.error('You must tick both consent boxes to continue');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/rsvp/${code}`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        birth_date: birthDate,
        agreed_eligibility: true,
        agreed_terms: true,
        terms_version: info?.event_terms_updated_at || null,
        party_type: partyType,
        gender,
        partner_name: partyType === 'couple' ? partnerName.trim() : null,
        partner_phone: partyType === 'couple' ? partnerPhone.trim() : null,
        partner_email: partyType === 'couple' && partnerEmail.trim() ? partnerEmail.trim() : null,
        partner_birth_date: partyType === 'couple' ? partnerBirthDate : null,
      });
      setResult(res.data);
      // Meta Lead — guest-list RSVP is a high-intent no-cost conversion.
      // We fire for both `confirmed` and `waitlisted` outcomes so the
      // pixel captures every real signup; `status` param lets you slice
      // in the Events Manager if you only want to count confirmed leads.
      trackLead({
        contentName: info?.event?.title || info?.list?.name,
        contentCategory: info?.event?.genre,
        status: res.data?.status,
      });
      // Fetch QR for display — only issued when guest is confirmed (stag males wait for promoter approval)
      if (res.data?.status === 'confirmed') {
        try {
          const qr = await axios.get(`${API}/rsvp/qr/${res.data.id}`);
          setQrDataUri(qr.data?.data_uri || null);
        } catch (qrErr) {
          if (process.env.NODE_ENV !== 'production') console.warn('QR fetch failed', qrErr);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'RSVP failed — try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#B5FF00]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="rsvp-error">
        <div className="max-w-md text-center border border-red-500/30 bg-red-500/5 p-8">
          <AlertCircle size={42} className="text-red-400 mx-auto mb-4" />
          <h1 className="font-['Josefin_Sans'] text-3xl text-white mb-3">INVALID CODE</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link to="/" className="btn-primary inline-flex">BACK TO UNIGHT</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-20 sm:pt-24 pb-12 sm:pb-16 px-4" data-testid="rsvp-page">
      <div className="container mx-auto max-w-2xl">
        <RsvpEventHeader info={info} minAge={minAge} eventWhen={eventWhen} />

        {/* RSVP form or success */}
        {result ? (
          <RsvpSuccessCard result={result} info={info} qrDataUri={qrDataUri} />
        ) : info.at_capacity && !info.wait_list_mode ? (
          <div className="border border-red-500/30 bg-red-500/5 p-8 text-center">
            <AlertCircle size={42} className="text-red-400 mx-auto mb-4" />
            <h2 className="font-['Josefin_Sans'] text-2xl text-white mb-3">SORRY — LIST IS FULL</h2>
            <p className="text-zinc-400">Try another promoter&apos;s code or grab a ticket on the events page.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="border border-zinc-800 bg-[#0A0A0A] p-6 space-y-5" data-testid="rsvp-form">
            <h2 className="font-['Josefin_Sans'] text-2xl text-white" data-testid="rsvp-person1-heading">
              {partyType === 'couple' ? 'PERSON 1 · YOUR DETAILS' : 'YOUR DETAILS'}
            </h2>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">FULL NAME *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="rsvp-name-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">MOBILE (WhatsApp) *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  placeholder="+91 99999 99999"
                  required
                  data-testid="rsvp-phone-input"
                />
                <p className="text-zinc-500 text-xs mt-1 font-mono">Include country code. Used for your QR pass.</p>
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">EMAIL *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  placeholder="you@example.com"
                  required
                  data-testid="rsvp-email-input"
                />
              </div>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                DATE OF BIRTH * <span className="text-[#B5FF00]">({minAge}+ event)</span>
              </label>
              <input
                type="date"
                min="1900-01-01"
                max={todayIso}
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className={`w-full h-12 bg-[#050505] border ${ageBlocked ? 'border-red-500' : 'border-zinc-800'} text-white px-4 focus:border-[#B5FF00] focus:outline-none`}
                required
                data-testid="rsvp-birth-date-input"
              />
              {ageBlocked && (
                <p className="text-red-400 text-xs mt-2 font-mono" data-testid="rsvp-age-error">
                  Sorry, you must be {minAge}+ to attend this event.
                </p>
              )}
              {calculatedAge !== null && !ageBlocked && (
                <p className="text-zinc-500 text-xs mt-1 font-mono">You&apos;re {calculatedAge} — you&apos;re good to go.</p>
              )}
            </div>

            {/* Party type selector */}
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">WHO&apos;S COMING? *</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[{ v: 'single', l: 'JUST ME' }, { v: 'couple', l: 'COUPLE' }].map((opt) => {
                  const active = partyType === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => { setPartyType(opt.v); setGender(''); }}
                      className={`p-3 border font-mono text-xs tracking-widest transition-colors ${
                        active ? 'border-[#B5FF00] bg-[#B5FF00]/5 text-[#B5FF00]' : 'border-zinc-800 bg-[#050505] text-zinc-400 hover:border-zinc-600'
                      }`}
                      data-testid={`rsvp-party-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  );
                })}
              </div>

              {partyType === 'single' ? (
                <div className="grid grid-cols-2 gap-2">
                  {[{ v: 'female', l: 'FEMALE' }, { v: 'male', l: 'MALE' }].map((opt) => {
                    const active = gender === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setGender(opt.v)}
                        className={`p-3 border font-mono text-xs tracking-widest transition-colors ${
                          active ? 'border-[#B5FF00] bg-[#B5FF00]/5 text-[#B5FF00]' : 'border-zinc-800 bg-[#050505] text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid={`rsvp-gender-${opt.v}`}
                      >
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    {[{ v: 'male_female', l: 'MALE + FEMALE' }, { v: 'female_female', l: 'FEMALE + FEMALE' }].map((opt) => {
                      const active = gender === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setGender(opt.v)}
                          className={`p-3 border font-mono text-xs tracking-widest transition-colors ${
                            active ? 'border-[#B5FF00] bg-[#B5FF00]/5 text-[#B5FF00]' : 'border-zinc-800 bg-[#050505] text-zinc-400 hover:border-zinc-600'
                          }`}
                          data-testid={`rsvp-couple-${opt.v}`}
                        >
                          {opt.l}
                        </button>
                      );
                    })}
                  </div>
                  <h3
                    className="font-['Josefin_Sans'] text-lg text-white mt-4 mb-2 pt-3 border-t border-zinc-800"
                    data-testid="rsvp-person2-heading"
                  >
                    PERSON 2 · PARTNER DETAILS
                  </h3>
                  <input
                    type="text"
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    placeholder="Second person's full name *"
                    className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                    data-testid="rsvp-partner-name-input"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    <input
                      type="tel"
                      value={partnerPhone}
                      onChange={(e) => setPartnerPhone(e.target.value)}
                      placeholder="Partner mobile (WhatsApp) *"
                      className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                      data-testid="rsvp-partner-phone-input"
                    />
                    <input
                      type="email"
                      value={partnerEmail}
                      onChange={(e) => setPartnerEmail(e.target.value)}
                      placeholder="Partner email (optional)"
                      className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                      data-testid="rsvp-partner-email-input"
                    />
                  </div>
                  <div className="mt-2">
                    <label className="font-mono text-[10px] text-zinc-500 tracking-widest mb-1 block">
                      PARTNER DATE OF BIRTH * <span className="text-[#B5FF00]">({minAge}+)</span>
                    </label>
                    <input
                      type="date"
                      min="1900-01-01"
                      max={todayIso}
                      value={partnerBirthDate}
                      onChange={(e) => setPartnerBirthDate(e.target.value)}
                      className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                      data-testid="rsvp-partner-birth-date-input"
                    />
                  </div>
                </>
              )}
              {partyType === 'single' && gender === 'male' && (
                <p className="text-amber-400 text-xs mt-2 font-mono" data-testid="rsvp-stag-warning">
                  ⚠ Solo male entries go on the wait list. The promoter will approve you if space allows.
                </p>
              )}
            </div>

            {/* Consent checkboxes */}
            <div className="border-t border-zinc-800 pt-5 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="rsvp-eligibility-label">
                <input
                  type="checkbox"
                  checked={agreedEligibility}
                  onChange={(e) => setAgreedEligibility(e.target.checked)}
                  className="mt-1 w-5 h-5 bg-[#050505] border-zinc-800 text-[#B5FF00] focus:ring-[#B5FF00] flex-shrink-0"
                  data-testid="rsvp-eligibility-checkbox"
                />
                <span className="text-sm text-zinc-300 leading-relaxed">
                  I confirm I am <strong className="text-white">{minAge} or older</strong>, the details above are accurate,
                  and I will carry valid government ID to the venue.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="rsvp-terms-label">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-1 w-5 h-5 bg-[#050505] border-zinc-800 text-[#B5FF00] focus:ring-[#B5FF00] flex-shrink-0"
                  data-testid="rsvp-terms-checkbox"
                />
                <span className="text-sm text-zinc-300 leading-relaxed">
                  I have read and accept the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-[#B5FF00] underline hover:text-[#B5FF00]/80"
                    data-testid="rsvp-view-terms-btn"
                  >
                    {isCustomTerms ? 'event terms & conditions' : 'Unight terms'}
                  </button>
                  {' '}for this event.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || ageBlocked}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="rsvp-submit"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {info.at_capacity ? 'JOIN WAITLIST' : 'ADD ME TO THE LIST'}
            </button>
            <p className="text-zinc-500 font-mono text-xs tracking-wider text-center">
              Your details only reach this promoter and Unight. We&apos;ll WhatsApp + email your confirmation.
            </p>
          </form>
        )}
      </div>

      <RsvpTermsModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        eventTitle={info.event_title}
        termsText={termsText}
        isCustomTerms={isCustomTerms}
      />
    </div>
  );
};

export default RsvpPage;
